import { z } from 'zod';

export const RECEPTION_DAMAGE_ZONES = [
  'FRONT',
  'REAR',
  'LEFT_SIDE',
  'RIGHT_SIDE',
  'DASHBOARD',
  'OTHER',
] as const;

export const ReceptionDamageZoneSchema = z.enum(RECEPTION_DAMAGE_ZONES);
export type ReceptionDamageZone = z.infer<typeof ReceptionDamageZoneSchema>;

export const RECEPTION_DAMAGE_TYPES = ['SCRATCH', 'DENT', 'CRACK', 'BROKEN', 'MISSING'] as const;

export const RECEPTION_DAMAGE_SEVERITIES = ['LIGHT', 'MEDIUM', 'SEVERE'] as const;

export const ReceptionDamageTypeSchema = z.enum(RECEPTION_DAMAGE_TYPES);
export type ReceptionDamageType = z.infer<typeof ReceptionDamageTypeSchema>;

export const ReceptionDamageSeveritySchema = z.enum(RECEPTION_DAMAGE_SEVERITIES);
export type ReceptionDamageSeverity = z.infer<typeof ReceptionDamageSeveritySchema>;

export const RECEPTION_MEDIA_TYPES = ['IMAGE', 'VIDEO'] as const;
export const ReceptionMediaTypeSchema = z.enum(RECEPTION_MEDIA_TYPES);
export type ReceptionMediaType = z.infer<typeof ReceptionMediaTypeSchema>;

export const ReceptionDamagePhotoSchema = z.object({
  id: z.uuid(),
  storageKey: z.string().min(1),
  mediaType: ReceptionMediaTypeSchema.default('IMAGE'),
  contentType: z.string().min(1).nullable().default(null),
  objectUrl: z.url(),
  createdAt: z.iso.datetime(),
  /**
   * BR-SO-220: false means the photo has not been classified.
   * Sem avaria is classified true with a null damageType — the two states do not collapse.
   */
  classified: z.boolean(),
  damageType: ReceptionDamageTypeSchema.nullable(),
  severity: ReceptionDamageSeveritySchema.nullable(),
});

export type ReceptionDamagePhoto = z.infer<typeof ReceptionDamagePhotoSchema>;

export const RECEPTION_DAMAGE_CHECKLIST_PROBLEM_STATUSES = [
  'HALF_LIFE',
  'BALD',
  'BURNED_OUT',
  'DAMAGE',
  'NOT_WORKING',
] as const;

export const ReceptionDamageChecklistProblemStatusSchema = z.enum(
  RECEPTION_DAMAGE_CHECKLIST_PROBLEM_STATUSES,
);
export type ReceptionDamageChecklistProblemStatus = z.infer<
  typeof ReceptionDamageChecklistProblemStatusSchema
>;

export const ReceptionDamageChecklistOriginSchema = z.object({
  categoryKey: z.string().trim().min(1),
  itemKey: z.string().trim().min(1),
  position: z.string().trim().min(1).nullable(),
  /** BR-SO-200: last problem status; null on legacy rows. Optional for fixtures. */
  problemStatus: ReceptionDamageChecklistProblemStatusSchema.nullable().optional(),
});

export type ReceptionDamageChecklistOrigin = z.infer<typeof ReceptionDamageChecklistOriginSchema>;

export const ReceptionDamageSchema = z.object({
  id: z.uuid(),
  xPercent: z.number().int().min(0).max(100),
  yPercent: z.number().int().min(0).max(100),
  zone: ReceptionDamageZoneSchema.nullable().optional(),
  damageType: ReceptionDamageTypeSchema.nullable(),
  severity: ReceptionDamageSeveritySchema.nullable(),
  comment: z.string().nullable(),
  photos: z.array(ReceptionDamagePhotoSchema),
  /** BR-SO-196: present when the card was created from a checklist problem. Optional for fixtures. */
  checklistOrigin: ReceptionDamageChecklistOriginSchema.nullable().optional(),
});

export type ReceptionDamage = z.infer<typeof ReceptionDamageSchema>;

export const CreateReceptionDamageBodySchema = z.object({
  xPercent: z.number().int().min(0).max(100),
  yPercent: z.number().int().min(0).max(100),
  damageType: ReceptionDamageTypeSchema.optional(),
  severity: ReceptionDamageSeveritySchema.optional(),
  comment: z.string().trim().max(2000).nullable().optional(),
});

export type CreateReceptionDamageBody = z.infer<typeof CreateReceptionDamageBodySchema>;

export const CreateReceptionDamageResponseSchema = z.object({
  damageId: z.uuid(),
});

export type CreateReceptionDamageResponse = z.infer<typeof CreateReceptionDamageResponseSchema>;

export const UpdateReceptionDamageBodySchema = z
  .object({
    xPercent: z.number().int().min(0).max(100).optional(),
    yPercent: z.number().int().min(0).max(100).optional(),
    damageType: ReceptionDamageTypeSchema.nullable().optional(),
    severity: ReceptionDamageSeveritySchema.nullable().optional(),
    comment: z.string().trim().max(2000).nullable().optional(),
  })
  .refine(
    (body) =>
      body.xPercent !== undefined ||
      body.yPercent !== undefined ||
      body.damageType !== undefined ||
      body.severity !== undefined ||
      body.comment !== undefined,
    { message: 'At least one field is required' },
  );

export type UpdateReceptionDamageBody = z.infer<typeof UpdateReceptionDamageBodySchema>;

export const UpdateReceptionDamageResponseSchema = z.object({
  damageId: z.uuid(),
});

export type UpdateReceptionDamageResponse = z.infer<typeof UpdateReceptionDamageResponseSchema>;

export const DeleteReceptionDamageResponseSchema = z.object({
  damageId: z.uuid(),
});

export type DeleteReceptionDamageResponse = z.infer<typeof DeleteReceptionDamageResponseSchema>;

export const AddReceptionDamagePhotoBodySchema = z.object({
  storageKey: z.string().trim().min(1),
  /** Uploaded content-type — derives media_type (video/mp4 → VIDEO). Optional (defaults IMAGE). */
  contentType: z.string().trim().min(1).optional(),
});

export type AddReceptionDamagePhotoBody = z.infer<typeof AddReceptionDamagePhotoBodySchema>;

export const AddReceptionDamagePhotoResponseSchema = z.object({
  photoId: z.uuid(),
});

export type AddReceptionDamagePhotoResponse = z.infer<typeof AddReceptionDamagePhotoResponseSchema>;

export const DeleteReceptionDamagePhotoResponseSchema = z.object({
  photoId: z.uuid(),
});

export type DeleteReceptionDamagePhotoResponse = z.infer<
  typeof DeleteReceptionDamagePhotoResponseSchema
>;

export const UpdateReceptionDamagePhotoBodySchema = z.object({
  classified: z.boolean(),
  damageType: ReceptionDamageTypeSchema.nullable(),
  severity: ReceptionDamageSeveritySchema.nullable(),
});

export type UpdateReceptionDamagePhotoBody = z.infer<typeof UpdateReceptionDamagePhotoBodySchema>;

export const UpdateReceptionDamagePhotoResponseSchema = z.object({
  photoId: z.uuid(),
});

export type UpdateReceptionDamagePhotoResponse = z.infer<
  typeof UpdateReceptionDamagePhotoResponseSchema
>;
